/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import { Checkbox, Heading, Link, TextLink } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import {
  ExclamationIcon,
  InfoCircleSolidIcon,
  InfoIcon,
  QuestionCircleIcon,
} from '@mm/icons';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { usePersonasCRUDActions } from '@mm/state/actions/PersonasCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useUploadActions } from '@mm/state/actions/uploadActions';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import {
  PromoterCustomButton,
  PromoterModel,
} from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import noProfilePicIcon from 'assets/icons/contact_noimage.svg';
import { ReactComponent as GreenCheckCircle } from 'assets/icons/green-check-circle.svg';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ChannelSpecificContainer } from '../../../../../../common/components/ChannelSpecificContainer/ChannelSpecificContainer';
import { ImageWithFallback } from '../../../../../../common/components/ImageWithFallback/ImageWithFallback';
import { InputWithEmoji } from '../../../../../../common/components/InputWithEmoji/InputWithEmoji';
import { TypedPrimarySelect } from '../../../../../../common/components/PrimarySelect/TypedPrimarySelect';
import { PrimaryToolTip } from '../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { RequiredPlanBadge } from '../../../../../../common/components/ProBadge';
import { ProfilePictureEditor } from '../../../../../../common/components/ProfilePictureEditor/ProfilePictureEditor';
import RadioInput from '../../../../../../common/components/RadioInput';
import TextInput from '../../../../../../common/components/TextInput';
import { colors } from '../../../../beta/styles/colors';
import { ChatWidgetPreviewChannelOption } from '../ChatWidgetPreviewFrame/ChatWidgetPreviewFrame';
import { CustomerChatEditorAccordionItem } from '../CustomerChatEditorAccordionItem/CustomerChatEditorAccordionItem';
import { ChatWidgetGreetingButtonEditor } from './ChatWidgetGreetingButtonEditor/ChatWidgetGreetingButtonEditor';
import './CustomerChatGreetingEditor.pcss';
import { ReactComponent as WebchatGreetingTypeFirstMsgInDialogueIcon } from './greeting-style--first-msg-in-dialogue.svg';
import WebchatGreetingTypeInteractivePreview from './greeting-style--interactive--preview.png';
import WebchatGreetingTypeInteractiveWithButtonPreview from './greeting-style--interactive-with-button--preview.png';
import { ReactComponent as WebchatGreetingTypeInteractiveWithButtonIcon } from './greeting-style--interactive-with-button.svg';
import { ReactComponent as WebchatGreetingTypeInteractiveIcon } from './greeting-style--interactive.svg';
import { ReactComponent as WebchatGreetingTypeStaticPreview } from './greeting-style--static--preview.svg';
import { ReactComponent as WebchatGreetingTypeStaticIcon } from './greeting-style--static.svg';

type Props = {
  promoter: PromoterModel;
  setPreviewChannel: (channel: ChatWidgetPreviewChannelOption) => void;
  usedInSlideout?: boolean;
  onAlertTextClick?: () => void;
};

export const DEFAULT_WEBCHAT_INTERACTIVE_GREETING =
  'Hello! How can I help you today?';
const getInitialLocalPromoter = (promoter: PromoterModel) => {
  return {
    ...promoter,
    loggedInGreeting: promoter.loggedInGreeting || 'Hi! How can we help you?',
    loggedOutGreeting:
      promoter.loggedOutGreeting ||
      `Hi! We're here to answer any questions you may have`,
    color: promoter.color || '#0084ff',
    webchatGreeting:
      promoter.webchatGreeting ?? DEFAULT_WEBCHAT_INTERACTIVE_GREETING,
    greetingStyle: promoter.greetingStyle || 'inline_pic',
    webchatGreetWithStartbot:
      promoter.webchatGreetWithStartbot ?? promoter.greetWithStartbot,
    customButtons:
      promoter.customButtons || ([] as PromoterCustomButton[] | null),
    greetingInputPlaceholder:
      promoter.greetingInputPlaceholder ?? 'Send Message',
  };
};

type WebchatGreetingType =
  | 'static'
  | 'interactive'
  | 'interactive_with_button'
  | 'first_msg_in_dialogue';

const WEBCHAT_GREETING_TYPE_DATA: Record<
  WebchatGreetingType,
  { icon: React.ReactNode; label: string; preview?: React.ReactNode }
> = {
  static: {
    label: 'Static',
    icon: (
      <WebchatGreetingTypeStaticIcon className="customer-chat-greeting-editor__webchat-greeting-type-diagram" />
    ),
    preview: <WebchatGreetingTypeStaticPreview />,
  },
  interactive: {
    label: 'Interactive',
    icon: (
      <WebchatGreetingTypeInteractiveIcon className="customer-chat-greeting-editor__webchat-greeting-type-diagram" />
    ),
    preview: <img src={WebchatGreetingTypeInteractivePreview} alt="" />,
  },
  interactive_with_button: {
    label: 'Interactive with Button',
    icon: (
      <WebchatGreetingTypeInteractiveWithButtonIcon className="customer-chat-greeting-editor__webchat-greeting-type-diagram" />
    ),
    preview: (
      <img src={WebchatGreetingTypeInteractiveWithButtonPreview} alt="" />
    ),
  },
  first_msg_in_dialogue: {
    label: 'First msg in Dialogue',
    icon: (
      <WebchatGreetingTypeFirstMsgInDialogueIcon className="customer-chat-greeting-editor__webchat-greeting-type-diagram" />
    ),
    preview: (
      <div className="customer-chat-greeting-editor__webchat-greeting-type-preview-text">
        We’ll use the first message in your chat dialogue.
      </div>
    ),
  },
};

export const CustomerChatGreetingEditor = (props: Props) => {
  const { promoter, setPreviewChannel, usedInSlideout, onAlertTextClick } =
    props;

  const { id: botId } = useSelector(activeChatbotSelector);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const personas = useSelector((state: StoreState) => state.personas.entities);

  const canUseGreetingEditor = useSelector(
    combinedFeatureFlagsSelector,
  ).customerChatGreetingEditor;
  const modalActions = useModalActions();

  const { uploadImage } = useUploadActions();
  const promoterCRUDActions = usePromoterCRUDActions();
  const chatbotCRUDActions = useChatbotCRUDActions();
  const personasCRUDActions = usePersonasCRUDActions();

  const [localPromoter, setLocalPromoter] = useState(
    getInitialLocalPromoter(promoter),
  );
  const [openPopover, setOpenPopover] = useState<WebchatGreetingType>();

  const firstWidgetInDialogueSupported = useMemo(() => {
    if (widgets.length === 0 || !localPromoter.webchatGreetWithStartbot) {
      return;
    }
    const dialogueWidgets = widgets
      .filter(w => w.pageId === localPromoter.pageId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    return ['text', 'quick_question'].includes(dialogueWidgets[0]?.type);
  }, [widgets, localPromoter.webchatGreetWithStartbot, localPromoter.pageId]);
  const invalidWidgetForFirstMessageInDialogue =
    localPromoter.webchatGreetWithStartbot && !firstWidgetInDialogueSupported;
  const blankGreetingTextError =
    !localPromoter.webchatGreetWithStartbot &&
    localPromoter.webchatGreeting === '';

  // Fetch personas for agents associated with this FB page
  useEffect(() => {
    if (activeFacebookPage?.remoteId) {
      personasCRUDActions.retrieveAll(activeFacebookPage.remoteId);
    }
  }, [activeFacebookPage, personasCRUDActions]);

  // Update localPromoter whenever the original object changes
  useEffect(() => {
    setLocalPromoter(getInitialLocalPromoter(promoter));
  }, [promoter]);

  if (!activeFacebookPage) {
    return null;
  }

  const armedLandmine = (landmineName: string, operation: Function) => {
    if (!canUseGreetingEditor) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName,
        feature: 'customerChatGreetingEditor',
      });
      return;
    }
    operation();
  };

  const displayProfileOptions = [
    { id: -1, name: 'Add Custom' },
    {
      id: 0,
      name: activeFacebookPage.name || 'Page',
      picture: activeFacebookPage.squarePicture,
    },
    ...personas.map(p => ({
      id: p.userId,
      name: p.name,
      picture: p.profilePictureUrl,
    })),
  ];
  const selectedDisplayProfileOptionId =
    typeof promoter.customDisplayName === 'string'
      ? -1
      : promoter.displayUserId || 0;
  const selectedDisplayProfileOption =
    displayProfileOptions.find(p => p.id === selectedDisplayProfileOptionId) ||
    displayProfileOptions[0];

  const webchatGreetingType: WebchatGreetingType = (() => {
    switch (localPromoter.greetingStyle) {
      case 'static':
        return 'static';
      case 'inline_pic':
        if (localPromoter.webchatGreetWithStartbot) {
          return 'first_msg_in_dialogue';
        }
        if (localPromoter.customButtonsEnabled) {
          return 'interactive_with_button';
        }
        return 'interactive';
      default:
        return 'static';
    }
  })();

  const setWebchatGreetingType = (greetingType: WebchatGreetingType) => {
    if (greetingType === webchatGreetingType) {
      return;
    }
    switch (greetingType) {
      case 'interactive':
        return updatePromoterFields({
          greetingStyle: 'inline_pic',
          webchatGreetWithStartbot: false,
          customButtonsEnabled: false,
        });
      case 'interactive_with_button':
        return updatePromoterFields({
          greetingStyle: 'inline_pic',
          webchatGreetWithStartbot: false,
          customButtonsEnabled: true,
          ...(localPromoter.customButtons?.length === 0
            ? {
                customButtons: [
                  {
                    type: 'text',
                    title: "Let's chat!",
                  } as PromoterCustomButton,
                ],
              }
            : {}),
        });

      case 'first_msg_in_dialogue':
        return updatePromoterFields({
          greetingStyle: 'inline_pic',
          webchatGreetWithStartbot: true,
          customButtonsEnabled: false,
        });

      case 'static':
      default:
        return updatePromoterFields({
          greetingStyle: 'static',
          webchatGreetWithStartbot: false,
          customButtonsEnabled: false,
        });
    }
  };

  const updatePromoter = (updatedPromoter: PromoterModel) => {
    chatbotCRUDActions.updateSetupStepsRemainingByPageIdUpdated(
      updatedPromoter.pageId,
    );
    return promoterCRUDActions.update(botId, updatedPromoter);
  };

  const updatePromoterFields = (updatedFields: Partial<PromoterModel>) => {
    updatePromoter({ ...localPromoter, ...updatedFields });
  };
  const updateLocalPromoterFields = (updatedFields: Partial<PromoterModel>) => {
    setLocalPromoter({ ...localPromoter, ...updatedFields });
  };

  const selectDisplayProfileOption = (id: number) => {
    if (selectedDisplayProfileOptionId === id) {
      return;
    }
    if (id === 0) {
      updatePromoterFields({
        displayUserId: null,
        customDisplayImageId: null,
        customDisplayName: null,
      });
      return;
    }
    if (id === -1) {
      updatePromoterFields({
        displayUserId: null,
        customDisplayName: '',
      });
      return;
    }
    updatePromoterFields({
      displayUserId: id,
      customDisplayName: null,
      customDisplayImageId: null,
    });
  };

  const onUploadCustomPicture = async (imageFile: File) => {
    setPreviewChannel('web_chat');
    if (imageFile) {
      const extension = imageFile.type.split('/').pop();
      if (extension) {
        const imageAssetId = await uploadImage({
          content: imageFile,
          extension,
        });
        updatePromoterFields({
          customDisplayImageId: imageAssetId,
        });
      }
    }
  };

  return (
    <CustomerChatEditorAccordionItem
      title="Greeting Settings"
      descriptionText="Greeting text and design"
      facebook={!usedInSlideout}
      webchat={!usedInSlideout}
      openOnMount
      usedInSlideout={usedInSlideout}
    >
      <ChannelSpecificContainer
        channel="facebook"
        usedInSlideout={usedInSlideout}
        onAlertTextClick={onAlertTextClick}
      >
        <Heading size={4}>Facebook Greeting</Heading>

        <div>
          <RadioInput
            name="greetingMode"
            checked={!!localPromoter.greetWithStartbot}
            value="true"
            label={
              <div style={{ display: 'inline-block' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  Use first message in dialogue&nbsp;
                  <RequiredPlanBadge billingType={PageBillingType.Pro} />
                </div>
              </div>
            }
            onChange={() =>
              armedLandmine(
                landmineNames[
                  'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__EDIT-CUSTOMER-CHAT__GREET-WITH-STARTBOT'
                ],
                () => {
                  setPreviewChannel('facebook');
                  updatePromoterFields({
                    greetWithStartbot: true,
                  });
                },
              )
            }
          />
          <RadioInput
            name="greetingMode"
            checked={!localPromoter.greetWithStartbot}
            value="false"
            label="Static greeting"
            onChange={() => {
              setPreviewChannel('facebook');
              updatePromoterFields({ greetWithStartbot: false });
            }}
          />
        </div>
        {!localPromoter.greetWithStartbot && (
          <InputWithEmoji
            className="customer-chat-greeting-editor__greeting-input"
            textInputClassName="customer-chat-greeting-editor__greeting-input__text-input"
            multiline
            value={localPromoter.loggedOutGreeting}
            onChange={text =>
              updateLocalPromoterFields({ loggedOutGreeting: text })
            }
            onFocus={() => setPreviewChannel('facebook')}
            onBlur={() => updatePromoter(localPromoter)}
          />
        )}
      </ChannelSpecificContainer>

      <ChannelSpecificContainer
        channel="web_chat"
        usedInSlideout={usedInSlideout}
        onAlertTextClick={onAlertTextClick}
      >
        <Heading size={4} addMarginAbove={usedInSlideout}>
          Webchat Greeting
        </Heading>

        <div className="customer-chat-greeting-editor__info-block">
          {!usedInSlideout && <InfoIcon />}
          <div
            className={
              usedInSlideout
                ? 'customer-chat-greeting-editor__info-description'
                : ''
            }
          >
            You can customize the appearance of the widget in even more detail.
            Just click the edit icon, or do nothing to keep the default style.
          </div>
        </div>

        <div className="customer-chat-greeting-editor__webchat-greeting-types">
          {(
            [
              'static',
              'interactive',
              'interactive_with_button',
              'first_msg_in_dialogue',
            ] as WebchatGreetingType[]
          ).map((t, i) => (
            <div
              key={t}
              className={classNames(
                'customer-chat-greeting-editor__webchat-greeting-type',
                {
                  'customer-chat-greeting-editor__webchat-greeting-type--selected':
                    t === webchatGreetingType,
                  'customer-chat-greeting-editor__webchat-greeting-type--danger':
                    t === webchatGreetingType &&
                    (invalidWidgetForFirstMessageInDialogue ||
                      blankGreetingTextError),
                },
              )}
              onClick={() => {
                setPreviewChannel('web_chat');
                setWebchatGreetingType(t);
              }}
              onMouseOver={() => setOpenPopover(t)}
              onMouseLeave={() => setOpenPopover(undefined)}
            >
              <div className="customer-chat-greeting-editor__webchat-greeting-type-label">
                {WEBCHAT_GREETING_TYPE_DATA[t].label}
              </div>
              {WEBCHAT_GREETING_TYPE_DATA[t].icon}
              {t === webchatGreetingType &&
                (invalidWidgetForFirstMessageInDialogue ||
                blankGreetingTextError ? (
                  <ExclamationIcon
                    color={colors.danger}
                    className="customer-chat-greeting-editor__webchat-greeting-type-selected-indicator"
                  />
                ) : (
                  <GreenCheckCircle className="customer-chat-greeting-editor__webchat-greeting-type-selected-indicator" />
                ))}

              {!!WEBCHAT_GREETING_TYPE_DATA[t].preview && openPopover === t && (
                <div
                  className={classNames(
                    'customer-chat-greeting-editor__webchat-greeting-type-preview',
                    {
                      'customer-chat-greeting-editor__webchat-greeting-type-preview--first':
                        i === 0,
                    },
                    {
                      'customer-chat-greeting-editor__webchat-greeting-type-preview--last':
                        i ===
                        Object.keys(WEBCHAT_GREETING_TYPE_DATA).length - 1,
                    },
                  )}
                >
                  {WEBCHAT_GREETING_TYPE_DATA[t].preview}
                </div>
              )}
            </div>
          ))}
        </div>

        {blankGreetingTextError && (
          <TextLink
            icon={<ExclamationIcon color={colors.danger} />}
            className="customer-chat-greeting-editor__error-text-link"
          >
            Greeting Text cannot be empty. Click Customize to update.
          </TextLink>
        )}

        {invalidWidgetForFirstMessageInDialogue && (
          <TextLink
            icon={<ExclamationIcon color={colors.danger} />}
            className="customer-chat-greeting-editor__error-text-link"
          >
            Please use a Text or Quick Question widget as the first message in
            your Dialogue
          </TextLink>
        )}
        <div className="customer-chat-greeting-editor__webchat-custom-settings">
          <Heading size={4}>
            {`${WEBCHAT_GREETING_TYPE_DATA[webchatGreetingType].label} Settings`}
          </Heading>
          <>
            <Heading
              size={4}
              isBold={false}
              addMarginAbove
              className="customer-chat-greeting-editor____webchat-custom-settings-heading"
            >
              Display Profile
            </Heading>

            <TypedPrimarySelect
              className="customer-chat-greeting-editor__primary-select"
              activeItemClassName="customer-chat-greeting-editor__active-item"
              options={displayProfileOptions}
              value={selectedDisplayProfileOption}
              onChange={p => {
                setPreviewChannel('web_chat');
                if (p.id === -1) {
                  armedLandmine(
                    landmineNames[
                      'CUSTOMER-CHAT-WIDGET__CUSTOM-DISPLAY-PROFILE'
                    ],
                    () => selectDisplayProfileOption(p.id),
                  );
                } else {
                  selectDisplayProfileOption(p.id);
                }
              }}
              customOptionRenderer={o => (
                <div className="customer-chat-greeting-editor__persona-profile">
                  {o.id !== -1 && (
                    <ImageWithFallback
                      className="customer-chat-greeting-editor__persona-profile-image"
                      src={o.picture}
                      fallbackImage={noProfilePicIcon}
                      alt={JSON.stringify(o)}
                    />
                  )}
                  {o.name}
                  {o.id === -1 && (
                    <RequiredPlanBadge
                      className="customer-chat-greeting-editor__persona-profile-pro-badge"
                      billingType={PageBillingType.Pro}
                    />
                  )}
                </div>
              )}
            />
            {selectedDisplayProfileOptionId === -1 && (
              <>
                <Heading
                  size={4}
                  isBold={false}
                  addMarginAbove
                  className="customer-chat-greeting-editor____webchat-custom-settings-heading"
                >
                  Profile Image
                </Heading>
                <ProfilePictureEditor
                  profilePictureUrl={
                    localPromoter.customDisplayImageUrl ||
                    activeFacebookPage.squarePicture
                  }
                  onChange={onUploadCustomPicture}
                />{' '}
                <Heading
                  size={4}
                  isBold={false}
                  addMarginAbove
                  className="customer-chat-greeting-editor____webchat-custom-settings-heading"
                >
                  Display Name
                </Heading>
                <TextInput
                  className="customer-chat-greeting-editor__persona-profile-display-name-input"
                  value={localPromoter.customDisplayName || ''}
                  onFocus={() => setPreviewChannel('web_chat')}
                  onChange={e =>
                    updateLocalPromoterFields({
                      customDisplayName: e.target.value,
                    })
                  }
                  onBlur={() => updatePromoter(localPromoter)}
                />
              </>
            )}
            {selectedDisplayProfileOptionId !== 0 && (
              <Checkbox
                className="customer-chat-greeting-editor__hide-page-name-checkbox"
                isChecked={!!localPromoter.hidePageName}
                onToggle={e =>
                  updatePromoterFields({ hidePageName: !!e.target.checked })
                }
                label={`Hide "from ${activeFacebookPage.name}"`}
                labelHelpTextClassName="customer-chat-greeting-editor__hide-page-name-checkbox-help-text"
              />
            )}
          </>
          <Heading
            size={4}
            isBold={false}
            addMarginAbove
            className="customer-chat-greeting-editor____webchat-custom-settings-heading"
          >
            Greeting Text
          </Heading>

          {localPromoter.webchatGreetWithStartbot ? (
            <div className="customer-chat-greeting-editor__first-message-info-block">
              <InfoCircleSolidIcon />
              <div>
                We’ll use the first message in your chat dialogue.
                <br />
                <Link
                  className="customer-chat-greeting-editor__first-message-info-block-link"
                  to="#dialogue"
                >
                  Click here to edit your chat dialogue.
                </Link>
              </div>
            </div>
          ) : (
            <>
              <InputWithEmoji
                className={classNames(
                  'customer-chat-greeting-editor__greeting-input',
                  {
                    'customer-chat-greeting-editor__greeting-input--error':
                      localPromoter.webchatGreeting.trim().length === 0,
                  },
                )}
                textInputClassName="customer-chat-greeting-editor__greeting-input__text-input"
                multiline
                value={localPromoter.webchatGreeting}
                onChange={text =>
                  updateLocalPromoterFields({ webchatGreeting: text })
                }
                onFocus={() => setPreviewChannel('web_chat')}
                onBlur={() => updatePromoter(localPromoter)}
              />
            </>
          )}

          {localPromoter.customButtonsEnabled && localPromoter.customButtons ? (
            <>
              <Heading
                size={4}
                isBold={false}
                addMarginAbove
                className="customer-chat-greeting-editor____webchat-custom-settings-heading"
              >
                Greeting Buttons
              </Heading>
              <ChatWidgetGreetingButtonEditor
                buttons={localPromoter.customButtons}
                onChangeButtons={updatedButtons => {
                  setPreviewChannel('web_chat');
                  updatePromoterFields({
                    customButtons: updatedButtons,
                  });
                  setLocalPromoter({
                    ...localPromoter,
                    customButtons: updatedButtons,
                  });
                }}
              />
            </>
          ) : (
            localPromoter.greetingStyle !== 'static' && (
              <>
                <Heading
                  size={4}
                  isBold={false}
                  addMarginAbove
                  className="customer-chat-greeting-editor____webchat-custom-settings-heading"
                >
                  Placeholder Text
                </Heading>

                <InputWithEmoji
                  className="customer-chat-greeting-editor__placeholder-input"
                  textInputClassName="customer-chat-greeting-editor__placeholder-input__text-input"
                  value={localPromoter.greetingInputPlaceholder}
                  onChange={text =>
                    updateLocalPromoterFields({
                      greetingInputPlaceholder: text,
                    })
                  }
                  onFocus={() => setPreviewChannel('web_chat')}
                  onBlur={() => updatePromoter(localPromoter)}
                />
              </>
            )
          )}
        </div>
      </ChannelSpecificContainer>

      <RemoveBranding />
    </CustomerChatEditorAccordionItem>
  );
};

const RemoveBranding = () => {
  const planInfo = useSelector(
    (state: StoreState) => activeFacebookPagePlanInfoSelector(state).info,
  );

  const canRemoveMMBranding = useSelector(
    combinedFeatureFlagsSelector,
  ).customerChatEditorRemoveMMBranding;
  const modalActions = useModalActions();

  const onToggle = () => {
    if (!canRemoveMMBranding) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName:
          landmineNames[
            'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__EDIT-CUSTOMER-CHAT__REMOVE-MM-GREETING'
          ],
        feature: 'customerChatEditorRemoveMMBranding',
      });
    }
  };

  return (
    <div className="customer-chat-greeting-editor__remove-mobilemonkey-branding customer-chat-greeting-editor__space-before">
      <Checkbox
        className="customer-chat-greeting-editor__remove-mobilemonkey-branding-checkbox"
        isChecked={!planInfo.isFreePlan}
        disabled={!planInfo.isFreePlan}
        onToggle={onToggle}
        labelHelpTextClassName="customer-chat-greeting-editor__remove-branding-label"
        checkboxInputClassName="customer-chat-greeting-editor__remove-mobilemonkey-branding-checkbox-input"
        label="Remove Customers.ai branding"
      />
      <PrimaryToolTip
        align="center"
        position="top"
        classNameContent="customer-chat-greeting-editor__help-tooltip"
        anchorElement={
          <QuestionCircleIcon
            className="customer-chat-greeting-editor__help-tooltip-icon"
            color="#262626"
          />
        }
      >
        <strong>(Webchat only)</strong> Remove “Powered by Customers.ai” text on
        your chat window.
      </PrimaryToolTip>
      <RequiredPlanBadge billingType={PageBillingType.Pro} />
    </div>
  );
};
